import React, { useMemo, useRef, useState } from 'react';
import GlowCard from './GlowCard';
import Reveal from './Reveal';

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M20.52 3.48A11.8 11.8 0 0 0 12.08 0C5.57 0 .3 5.28.3 11.78c0 2.08.54 4.1 1.56 5.88L0 24l6.53-1.86a11.7 11.7 0 0 0 5.56 1.42h.01c6.5 0 11.78-5.28 11.78-11.78a11.7 11.7 0 0 0-3.36-8.3Zm-8.43 18.08h-.01a9.7 9.7 0 0 1-4.95-1.36l-.36-.21-3.87 1.1 1.04-3.77-.24-.39a9.77 9.77 0 0 1-1.5-5.15c0-5.4 4.4-9.79 9.8-9.79 2.61 0 5.06 1.01 6.9 2.86a9.7 9.7 0 0 1 2.88 6.93c0 5.4-4.4 9.79-9.79 9.79Zm5.37-7.35c-.29-.15-1.72-.85-1.99-.95-.27-.1-.47-.15-.66.15-.2.29-.76.95-.93 1.14-.17.2-.34.22-.63.08-.29-.15-1.22-.45-2.32-1.43-.86-.77-1.44-1.72-1.6-2.01-.17-.29-.02-.45.13-.6.13-.13.29-.34.44-.51.15-.17.2-.29.29-.49.1-.2.05-.37-.02-.52-.08-.15-.66-1.59-.91-2.18-.24-.58-.49-.5-.66-.5h-.56c-.2 0-.52.08-.79.37-.27.29-1.03 1.01-1.03 2.46 0 1.45 1.05 2.86 1.2 3.06.15.2 2.05 3.13 4.96 4.39.69.3 1.24.48 1.66.62.7.22 1.33.19 1.83.11.56-.08 1.72-.7 1.96-1.37.24-.67.24-1.25.17-1.37-.07-.12-.27-.2-.56-.34Z" />
  </svg>
);

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 10V16M12 7.5h.01" />
  </svg>
);

const CopIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="8" />
    <path d="M9.2 9.6c.4-1 1.4-1.6 2.8-1.6 1.8 0 3 1.1 3 2.5 0 1.6-1.3 2.3-2.6 2.6l-1 .2M11.5 16h.01" />
  </svg>
);

const UsdIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 3V21" />
    <path d="M16.5 7.2c0-1.4-1.3-2.4-3-2.4s-3 .9-3 2.3c0 3.3 6 1.7 6 5.2 0 1.4-1.3 2.6-3.3 2.6-1.9 0-3.2-1-3.2-2.5" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.3">
    <path d="M5 13.4L9.2 17 19 7.5" />
  </svg>
);

const rawPlans = [
  {
    id: '1m',
    name: '1 Mes',
    months: 1,
    cop: 50000,
    usd: 13,
    label: 'Entrada',
    description: 'Ideal para probar el servicio y validar compatibilidad.',
    waLink: 'https://wa.link/41f3vv',
  },
  {
    id: '4m',
    name: '4 Meses',
    months: 4,
    cop: 160000,
    usd: 42,
    label: 'Popular',
    description: 'Balance entre ahorro y flexibilidad.',
    popular: true,
    waLink: 'https://wa.link/ceic3w',
  },
  {
    id: '6m',
    name: '6 Meses',
    months: 6,
    cop: 220000,
    usd: 60,
    label: 'Smart',
    description: 'Excelente costo por mes para uso continuo.',
    waLink: 'https://wa.link/einh5t',
  },
  {
    id: '1y',
    name: '1 Año',
    months: 12,
    cop: 380000,
    usd: 100,
    label: 'Maximo Ahorro',
    description: 'Plan anual para quienes quieren precio congelado.',
    waLink: 'https://wa.link/v2v9hz',
  },
];

const Pricing = () => {
  const baseMonthlyCop = rawPlans[0].cop;
  const [activePlan, setActivePlan] = useState('4m');
  const cardRefs = useRef({});
  const copFormat = useMemo(() => new Intl.NumberFormat('es-CO'), []);

  const plans = useMemo(() => {
    return rawPlans.map((plan) => {
      const monthlyCop = Math.round(plan.cop / plan.months);
      const monthlyUsd = Number((plan.usd / plan.months).toFixed(2));
      const savings = Math.max(0, baseMonthlyCop * plan.months - plan.cop);
      return { ...plan, monthlyCop, monthlyUsd, savings };
    });
  }, [baseMonthlyCop]);

  const formatCop = (value) => copFormat.format(value);

  const handleSelectPlan = (planId) => {
    setActivePlan(planId);
    window.requestAnimationFrame(() => {
      const target = cardRefs.current[planId];
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      window.setTimeout(() => target.focus({ preventScroll: true }), 200);
    });
  };

  return (
    <section className="mx-auto max-w-6xl px-4 pb-16 pt-6 text-center md:px-6">
      <h2 className="font-display text-3xl font-extrabold text-text-100 md:text-4xl">Nuestros Planes</h2>
      <p className="mx-auto mt-3 max-w-2xl text-sm text-text-300 md:text-base">
        Toca cualquier plan para enfocarlo y ver el desglose completo.
      </p>

      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        {plans.map((plan) => (
          <button
            key={plan.id}
            type="button"
            onClick={() => handleSelectPlan(plan.id)}
            className={`rounded-full border px-4 py-2 text-sm font-semibold transition duration-200 ${
              activePlan === plan.id
                ? 'border-brand bg-brand text-white shadow-[0_0_22px_rgba(255,122,26,0.35)]'
                : 'border-brand/30 bg-surface-800 text-text-300 hover:border-brand/55 hover:text-text-100'
            }`}
          >
            {plan.name}
          </button>
        ))}
      </div>

      <div className="mb-10 mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        {plans.map((plan, index) => {
          const isActive = activePlan === plan.id;
          return (
            <Reveal key={plan.id} delay={80 + index * 90}>
              <GlowCard
                as="article"
                ref={(node) => { cardRefs.current[plan.id] = node; }}
                tabIndex={0}
                role="button"
                onClick={() => handleSelectPlan(plan.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleSelectPlan(plan.id);
                  }
                }}
                className={`rounded-3xl border bg-surface-800 p-5 text-left shadow-[0_24px_44px_rgba(0,0,0,0.38)] focus:outline-none focus:ring-2 focus:ring-brand/60 ${
                  isActive
                    ? 'border-brand shadow-[0_24px_54px_rgba(194,83,0,0.35)] scale-[1.01]'
                    : 'border-brand/25 hover:-translate-y-0.5'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="rounded-full border border-brand/40 bg-brand/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-brand">
                    {plan.label}
                  </span>
                  {plan.popular && (
                    <span className="rounded-full bg-brand px-2.5 py-1 text-[11px] font-bold text-white">Top</span>
                  )}
                </div>

                <h3 className="mt-4 font-display text-2xl font-bold text-text-100">{plan.name}</h3>
                <p className="mt-1 text-xs text-text-300">{plan.description}</p>

                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between rounded-2xl border border-brand/20 bg-surface-700/70 px-4 py-3">
                    <span className="inline-flex items-center gap-2 text-xs text-text-300">
                      <CopIcon />
                      COP
                    </span>
                    <p className="font-display text-2xl font-extrabold text-brand">${formatCop(plan.cop)}</p>
                  </div>
                  <div className="flex items-center justify-between rounded-2xl border border-brand/20 bg-surface-700/70 px-4 py-3">
                    <span className="inline-flex items-center gap-2 text-xs text-text-300">
                      <UsdIcon />
                      USD
                    </span>
                    <p className="font-display text-2xl font-extrabold text-text-100">${plan.usd}</p>
                  </div>
                </div>

                <div className={`plan-details ${isActive ? 'is-open' : ''}`}>
                  <div>
                    <div className="grid gap-2 rounded-xl border border-brand/20 bg-black/25 p-3 text-xs text-text-300">
                      <p className="flex items-center justify-between">
                        <span>Equivalente mensual COP</span>
                        <strong className="text-text-100">${formatCop(plan.monthlyCop)}</strong>
                      </p>
                      <p className="flex items-center justify-between">
                        <span>Equivalente mensual USD</span>
                        <strong className="text-text-100">${plan.monthlyUsd}</strong>
                      </p>
                      <p className="flex items-center justify-between">
                        <span>Ahorro vs pago mensual</span>
                        <strong className="text-brand">${formatCop(plan.savings)}</strong>
                      </p>
                    </div>

                    <ul className="mt-3 space-y-2 text-sm text-text-300">
                      <li className="flex items-center gap-2">
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand/20 text-brand">
                          <CheckIcon />
                        </span>
                        Hasta 3 dispositivos simultaneos
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand/20 text-brand">
                          <CheckIcon />
                        </span>
                        Soporte por WhatsApp y acompanamiento de instalacion
                      </li>
                    </ul>
                  </div>
                </div>

                <a
                  href={plan.waLink}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(event) => event.stopPropagation()}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#1FA855] px-4 py-3 text-sm font-bold text-white transition duration-200 hover:bg-[#168947]"
                >
                  <WhatsAppIcon />
                  Comprar por WhatsApp
                </a>
              </GlowCard>
            </Reveal>
          );
        })}
      </div>

      <Reveal delay={180}>
        <GlowCard as="aside" className="mx-auto max-w-4xl rounded-2xl border border-brand/30 bg-surface-800/95 p-6 text-left">
          <h4 className="mb-3 inline-flex items-center gap-2 font-display text-lg font-bold text-text-100">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand/15 text-brand">
              <InfoIcon />
            </span>
            Informacion sobre las apps
          </h4>
          <p className="text-sm text-text-300 md:text-base">
            El unico sobrecosto es el de la aplicacion que aloja la senal en Smart TV y TV Box. Usualmente estas apps
            tienen costo anual por dispositivo en la tienda del televisor.
          </p>
          <p className="mt-3 text-sm font-semibold text-text-100 md:text-base">
            En celulares, la aplicacion de reproduccion suele ser gratuita.
          </p>
        </GlowCard>
      </Reveal>
    </section>
  );
};

export default Pricing;
